import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

// Third-party packages.
import * as dayjs from 'dayjs';
import { CSVLink } from 'react-csv';
import { Column, usePagination, useSortBy, useTable } from 'react-table';

// Types
import LoginsDataTable from "../../../types/LoginsDataTable";

// Helpers
import { getDefaultPageSize } from "../../../helpers/utils";

// Components.
import NoData from '../../../component/NoData';
import TablePagination from '../../../component/TablePagination';
import SortClass from "../../../component/SortClass";
import LoginTags from "../../../types/LoginsTags";
import ConditionalRender from "../../../helpers/ConditionalRender";

function LoginsList(props: {
    loading: boolean,
    loginDataTable: LoginsDataTable[],
    tags: LoginTags[],
    isAdmin: boolean
}) {

    const navigate = useNavigate();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const [tableFilter, setTableFilter] = useState(query.get("filter") || "");
    const initialPageIndex = Number(query.get("page") || 0);
    const initialPageSize = Number(query.get("pageSize") || getDefaultPageSize());

    function clearTableFilter() {

        // Clear any pre-existing filter values.
        setTableFilter('');
    }

    const data = useMemo(() => props.loginDataTable.filter(row => ((row.name.toLowerCase().includes(tableFilter.toLowerCase())))), [props.loginDataTable, tableFilter]);

    const columns: Column[] = useMemo(() => [
        {
            accessor: 'id',
            disableSortBy: true
        },
        {
            Header: 'Name',
            accessor: 'name',
            disableSortBy: true
        },
        {
            Header: 'Type',
            accessor: 'admin',
            defaultCanSort: true,
            Cell: ({ cell }) => (
                cell.row.values.admin ? 'Administrator' : ''
            )
        },
        {
            Header: 'Tags',
            accessor: 'tags',
            canSort: false,
            defaultCanSort: false,
            Cell: ({ cell }) => {
                if (props.tags.length) {
                    const tags = props.tags.filter(tag => tag.loginid === cell.row.values.id);
                    return tags.length ? tags.map(tag => `${tag.tagname}:${tag.tagvalue}`).join('; ') : '';
                }
                return '';
            }
        },
        ...(props.isAdmin ? [{
            Header: 'Actions',
            accessor: 'actions',
            className: 'actions',
            disableSortBy: true,
            headerClassName: 'actions',
            Cell: ({ cell }: any) => (
                <React.Fragment>
                    <Link to={`/admin/logins/edit/${cell.row.values.id}`} role="button"
                          className="btn btn-sm btn-primary">
                        <i className="fal fa-edit fa-fw"/><span>Edit</span>
                    </Link>
                    <Link to={`/admin/logins/delete/${cell.row.values.id}`} role="button"
                          className="btn btn-sm btn-danger">
                        <i className="fal fa-trash-alt fa-fw"/><span>Delete</span>
                    </Link>
                </React.Fragment>
            )
        }] : [])
    ], [props.isAdmin, props.tags]);


    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize }
    } = useTable({
            columns,
            data,
            initialState: {
                hiddenColumns: ['id'],
                pageIndex: initialPageIndex,
                pageSize: initialPageSize,
                sortBy: [{ id: 'name', desc: false }]
            }
        },
        useSortBy, usePagination);

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        if (tableFilter) {
            params.set('filter', tableFilter);
        } else {
            params.delete('filter');
        }

        params.set('page', pageIndex.toString());
        params.set('pageSize', pageSize.toString());

        navigate({ search: params.toString() }, { replace: true });
    }, [tableFilter, pageIndex, pageSize]);

    useEffect(() => {
        if (tableFilter) {
            gotoPage(0);
        }
    }, [tableFilter]);

    return (
        <div className="col h-100">
            <div className="card">
                <div className="card-header">
                    <i className="fal fa-users fa-fw" aria-hidden="true"></i>
                    Logins
                    <span className="badge bg-info"
                          data-tip="Total logins count">{data.length.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
                    <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                       data-bs-target="#collapseLogins" aria-expanded="false" aria-controls="collapseLogins"></i>
                </div>
                <div id="collapseLogins" className="card-body collapse show">
                    <div className="row row-cols-1 row-cols-md-2 table-search">
                        <div className="col col-md-11">
                            <ConditionalRender if={props.isAdmin}>
                                <Link to="/admin/logins/create" role="button" className="btn btn-sm btn-success">
                                    <i className="fal fa-plus-square fa-fw"></i><span>Create</span>
                                </Link>
                            </ConditionalRender>
                            <CSVLink role="button"
                                     headers={[{ label: 'ID', key: 'id' }, {
                                         label: 'Name',
                                         key: 'name'
                                     }, { label: 'Admin', key: 'admin' }]}
                                     data={data}
                                     download={`DBmarlin - ${dayjs.default().format('YYYY-MM-DD HH:mm')} - Logins.csv`}
                                     className="btn btn-sm btn-primary">
                                <i className="fal fa-file-export fa-fw"></i><span>Export</span>
                            </CSVLink>
                            <button className="btn btn-sm btn-dark" onClick={clearTableFilter}><i
                                className="far fa-undo"></i><span>Clear</span></button>
                        </div>
                        <div className="col col-md-2">
                            <input type="text" autoFocus className="form-control form-control-sm" placeholder="Search"
                                   value={tableFilter} data-lpignore={true}
                                   onChange={(e) => setTableFilter(e.target.value)}/>
                        </div>
                    </div>
                    <div className="table-responsive">
                        <table {...getTableProps()} className="table">
                            <thead>
                            {headerGroups.map(headerGroup => (
                                <tr {...headerGroup.getHeaderGroupProps()}>
                                    {headerGroup.headers.map(column => (
                                        <th {...column.getHeaderProps(column.getSortByToggleProps())}
                                            className={(column as any).className}>
                                            {column.render('Header')}
                                            <SortClass column={column}/>
                                        </th>
                                    ))}
                                </tr>
                            ))}
                            </thead>
                            <tbody {...getTableBodyProps()}>
                            {page.map(row => {
                                prepareRow(row)
                                return (
                                    <tr {...row.getRowProps()}>
                                        {row.cells.map((cell) => {
                                            return (
                                                <td {...cell.getCellProps()} className={(cell.column as any).className}>
                                                    {cell.render('Cell')}
                                                </td>
                                            )
                                        })}
                                    </tr>
                                )
                            })}
                            </tbody>
                        </table>
                        <NoData
                            error={null}
                            loading={props.loading}
                            length={data.length}
                        />
                        <TablePagination
                            length={data.length}
                            pageSize={pageSize}
                            setPageSize={setPageSize}
                            pageOptions={pageOptions}
                            pageCount={pageCount}
                            canPreviousPage={canPreviousPage}
                            previousPage={previousPage}
                            canNextPage={canNextPage}
                            nextPage={nextPage}
                            gotoPage={gotoPage}
                            pageIndex={pageIndex}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default LoginsList;
